import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import './WhyUs.css';

const whyUsFeatures = [
  {
    title: 'Instant Spec Sheets',
    description:
      'Complete technical documentation, engineering drawings, and installation guides available on demand. No waiting, no chasing reps.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
  {
    title: 'Local Warehouse Stock',
    description:
      'Inventory stocked locally for fast fulfillment. No waiting weeks for shipments — get what you need, when you need it.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    title: 'Wholesale Trade Pricing',
    description:
      'Competitive wholesale rates and volume discounts designed specifically for contractors and builders. Better margins on every job.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    title: 'Training & Certification',
    description:
      'Hands-on installation training and certification programs to keep your team at the forefront of heat pump technology.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c3 3 12 3 12 0v-5" />
      </svg>
    ),
  },
  {
    title: 'Expert Tech Support',
    description:
      'Certified technicians available for system selection, sizing calculations, and troubleshooting assistance whenever you need it.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    title: 'New England Coverage',
    description:
      'Serving Massachusetts, New Hampshire, and the broader New England region with dedicated local support and fast delivery.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="10" r="3" />
        <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" />
      </svg>
    ),
  },
];

const WhyUs: React.FC = () => {
  return (
    <div className="why-us-page">
      <Hero
        title="Why Lexington Air Systems"
        subtitle="More than a distributor — we're your competitive advantage in the heat pump market."
        background="gradient-teal"
        fullHeight={false}
      />

      <section className="section section--dark">
        <div className="container">
          <div className="why-us__grid">
            {whyUsFeatures.map((feature) => (
              <div key={feature.title} className="why-us__card">
                <div className="why-us__icon">{feature.icon}</div>
                <h3 className="why-us__card-title">{feature.title}</h3>
                <p className="why-us__card-text">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="section section--charcoal">
        <div className="container text-center">
          <span className="section__label">Partners & Certifications</span>
          <h2 className="section__title">Industry-Leading Partners</h2>
          <p className="section__subtitle">
            We partner with the best in the industry to bring you certified, rebate-eligible heat pump systems.
          </p>
          <div className="partners" style={{ marginTop: 'var(--space-2xl)' }}>
            <img src={process.env.PUBLIC_URL + "/imgs/partners/1-tcl-logo.png"} alt="TCL" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/6-Energy_Star_logo.png"} alt="Energy Star" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/7-AHRI-1080.webp"} alt="AHRI" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/4-MassSave.png"} alt="Mass Save" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/5-NEHP_logo.png"} alt="NEHPA" className="partners__img" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section--dark">
        <div className="container text-center">
          <h2 className="section__title">Ready to Partner with Us?</h2>
          <p className="section__subtitle">
            Join our network of contractors and builders benefiting from premium products and dedicated support.
          </p>
          <div className="btn-group">
            <Link to="/contact" className="btn btn--primary btn--lg">Contact Us</Link>
            <Link to="/products" className="btn btn--outline-white btn--lg">Browse Products</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
